"use client";

import { IVideo } from "@/shared/models/content/videos";
import Link from "next/link";
import Image from "next/image";
import type { IFocusArea } from "@/shared/models/focus-area.model";
import { getMediaUrl } from "@/lib/api/getUrl";
import { cleanTextFormatting } from "@/shared/utils/blog-utils";
import RecordAwareImage from "../blog/RecordAwareImage";
import { getVideoThumbnailCandidates, getVideoWatchHref } from "./videoPresentation";
import { rememberVideoViewerTrigger } from "./videoViewerFocus";
import type { VideoAuthor } from "./videoAuthor";
import styles from "./video-library.module.scss";

export type VideoCardNodeIds = {
  card: string;
  image: string;
  play: string;
  playIcon: string;
  playLabel: string;
  title: string;
  description: string;
  category: string;
  avatar: string;
};

function getAuthorInitials(name: string) {
  if (!name) return "AMA";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "AMA";
}

export default function VideoLibraryItem({
  video,
  focusArea,
  author,
  libraryHref,
  nodeIds
}: {
  video: IVideo;
  focusArea: IFocusArea | undefined;
  author: VideoAuthor | undefined;
  libraryHref: string;
  nodeIds: VideoCardNodeIds;
}) {
  const professionalUrl = author?.profileUrl || null;
  const professionalName = author?.name || "";
  const authorLabel = professionalName || "AMA author";
  const authorInitials = getAuthorInitials(professionalName);
  const watchHref = getVideoWatchHref(video, libraryHref);
  const description = cleanTextFormatting(video.description || "");
  const avatar = (
    <RecordAwareImage
      candidates={author?.photoFileName ? [getMediaUrl(author.photoFileName) || ""] : []}
      alt={authorLabel}
      className={styles.avatar}
      fallbackClassName={styles.avatarFallback}
      fallbackLabel={authorInitials}
      dataNodeId={nodeIds.avatar}
      sizes="36px"
    />
  );

  return (
    <article className={styles.videoCard} data-node-id={nodeIds.card}>
      <Link
        className={styles.videoMedia}
        href={watchHref}
        aria-label={`Play ${video.title}`}
        data-node-id={nodeIds.image}
        data-video-trigger={`${video.id}:media`}
        onClick={() => rememberVideoViewerTrigger(`${video.id}:media`)}
      >
        <RecordAwareImage
          candidates={getVideoThumbnailCandidates(video, focusArea)}
          alt={`Video thumbnail for ${video.title}`}
          className={styles.videoImage}
          fallbackClassName={styles.videoImageFallback}
          fallbackLabel={video.title}
          dataNodeId={nodeIds.image}
          sizes="(max-width: 767px) calc(100vw - 62px), 650px"
        />
        <span className={styles.playControl} data-node-id={nodeIds.play}>
          {/* Figma-exported node 863:49902. */}
          <Image src="/ama-approved/pages/video/play-circle.svg" alt="" width={20} height={20} loading="eager" data-node-id={nodeIds.playIcon} />
          <span data-node-id={nodeIds.playLabel}>Play</span>
        </span>
      </Link>
      <div className={styles.videoMetaRow}>
        <div className={styles.videoText}>
          <h2 className={styles.videoTitle} data-node-id={nodeIds.title}>
            <Link
              href={watchHref}
              data-video-trigger={`${video.id}:title`}
              onClick={() => rememberVideoViewerTrigger(`${video.id}:title`)}
            >
              {video.title}
            </Link>
          </h2>
          {description && <p className={styles.videoDescription} data-node-id={nodeIds.description}>{description}</p>}
        </div>
        <div className={styles.videoDetails}>
          {focusArea && (
            <Link className={styles.categoryLink} href={`/video-library/${focusArea.slug}`} data-node-id={nodeIds.category}>
              {focusArea.name}
            </Link>
          )}
          {professionalUrl ? (
            <Link className={styles.avatarLink} href={professionalUrl} aria-label={`View ${professionalName}'s profile`} data-node-id={nodeIds.avatar}>
              {avatar}
            </Link>
          ) : (
            <span className={styles.avatarLink} data-node-id={nodeIds.avatar}>
              {avatar}
            </span>
          )}
        </div>
      </div>
    </article>
  );
}
